import { TmElement } from '@root/packages/lit-sdk/src';
import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { translate } from '@root/packages/translations';
import { tagName } from '../../helpers';
import { styles } from './text-collapse.styles';

const tag = tagName('text-collapse');

@customElement(tag)
export class TextCollapseComponent extends TmElement {
  static override styles = styles;

  @property({ type: Number })
  lines = 3;

  @query('a')
  button!: HTMLElement;

  @query('content')
  content!: HTMLElement;

  @state()
  collapsable = false;

  @state()
  collapsed = true;

  #resizeObserver?: ResizeObserver;

  protected override updated() {
    this.#updateCollapsable();
  }

  protected override firstUpdated() {
    this.#resizeObserver = new ResizeObserver(this.#updateCollapsable);
    this.#resizeObserver.observe(this.content);
    this.button.addEventListener('keydown', this.keydownHandler);
  }

  protected override onDestroy = () => {
    this.#resizeObserver?.disconnect();
    this.button.removeEventListener('keydown', this.keydownHandler);
  };

  #updateCollapsable = () => {
    if (this.collapsed) {
      this.collapsable = this.content.offsetHeight < this.content.scrollHeight;
    }
  };

  override render() {
    return html`
      <main>
        <content style="--lines: ${this.collapsed ? this.lines : 99999}"
          ><slot></slot
        ></content>
        ${this.#buttonTemplate()}
      </main>
    `;
  }

  #buttonTemplate() {
    return html`<footer
      class="${this.collapsable ? 'collapsable' : 'disabled'}"
    >
      <a tabindex="0" @click="${this.#toggle}">${this.#linkTemplate()}</a>
    </footer>`;
  }

  #linkTemplate() {
    if (this.collapsed) {
      return html`<slot name="show-more">${translate('common.show_more')}</slot>
        <a-icon name="chevron-down"></a-icon>`;
    }
    return html`<slot name="show-less">${translate('common.show_less')}</slot>
      <a-icon name="chevron-up"></a-icon>`;
  }

  private keydownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).click();
    }
  };

  #toggle() {
    this.collapsed = !this.collapsed;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-text-collapse': TextCollapseComponent;
  }
}
